/**
 * PageHeader — consistent Materio-style page header used across all pages.
 * Renders an icon + title + subtitle with optional breadcrumb and right-side actions.
 */

import React from 'react';
import { NavLink } from 'react-router-dom';

export interface Breadcrumb {
  label: string;
  to?: string;
}

interface PageHeaderProps {
  icon: string;           // Remixicon class e.g. "ri-shield-check-line"
  title: string;
  subtitle?: string;
  breadcrumbs?: Breadcrumb[];
  actions?: React.ReactNode;
  colour?: string;        // bg-label-* colour name, default "primary"
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  icon,
  title,
  subtitle,
  breadcrumbs,
  actions,
  colour = 'primary',
}) => (
  <div className="np-page-head">
    <div className="d-flex align-items-center gap-3">
      <span className={`np-icon-badge np-tone-${colour}`}>
        <i className={icon}></i>
      </span>

      <div>
        {breadcrumbs && breadcrumbs.length > 0 && (
          <nav aria-label="breadcrumb" className="np-crumb">
            {breadcrumbs.map((crumb, i) => {
              const isLast = i === breadcrumbs.length - 1;
              return (
                <React.Fragment key={i}>
                  {i > 0 && <span className="np-crumb-sep">›</span>}
                  {crumb.to && !isLast
                    ? <NavLink to={crumb.to}>{crumb.label}</NavLink>
                    : <span className={isLast ? 'np-crumb-current' : ''}>{crumb.label}</span>}
                </React.Fragment>
              );
            })}
          </nav>
        )}
        <h4>{title}</h4>
        {subtitle && <p className="np-sub">{subtitle}</p>}
      </div>
    </div>

    {actions && <div className="np-page-actions">{actions}</div>}
  </div>
);
